import { useCallback } from 'react';
import Confetti from 'react-confetti';
import Toggle from '../../../common/Toggle';
import ContactSupportLink from '../../../components/ContactSupportLink';
import CopyToClipboardLink from '../../../components/CopyToClipboardLink';
import Error from '../../../components/Error';
import ExternalLink from '../../../components/ExternalLink';
import Header from '../../../components/Header';
import HtmlTagName from '../../../components/HtmlTagName';
import Loader from '../../../components/Loader';
import { Pill } from '../../../components/Pill';
import SnippetBox from '../../../components/SnippetBox';
import { SnippetContainerBox, SnippetFlexWrapper } from '../../../components/SnippetContainer';
import Tabs from '../../../components/Tabs';
import { ROUTES } from '../../../routes/routes';
import { CustomerPortal } from '../../../types/customerPortal';
import { Event } from '../../../types/event';
import { FeatureIcon } from '../../../types/pricingPage';
import { SNIPPET_SERVER_URL } from '../../../utils/constants';
import { iconToUrl } from '../../../utils/features';
import { useAxios, useCopySnippet } from '../../../utils/hooks';
import Footer from '../../PricingPages/NewPage/Footer';

const getCustomerPortalScript = (customerPortal: CustomerPortal) =>
    `<script>window.pricewell = [{name: 'track', args: ['load', 'portal', '${customerPortal.id}']}];</script>
<script src="${SNIPPET_SERVER_URL}/checkout.js"></script>`;
const getCustomTriggerPortalScript = (customerPortal: CustomerPortal) =>
    `window.pricewell.customerPortal('${customerPortal.id}', {email: 'CUSTOMER_EMAIL_HERE', stripeId: 'OR_STRIPE_ID_HERE'});`;
export const getCustomerPortalSnippet = (customerPortal: CustomerPortal, type: 'email' | 'stripeId') =>
    `<div id="pricewell-customer-portal" ${
        customerPortal.emailAuth
            ? ''
            : type === 'email'
            ? 'data-email="CUSTOMER_EMAIL_HERE"'
            : 'data-stripe-id="CUSTOMER_STRIPE_ID_HERE"'
    } data-id="${customerPortal.id}"></div>
<script src="${SNIPPET_SERVER_URL}/customer-portal.js"></script>`;

interface Props {
    customerPortal: CustomerPortal;
    onUpdate: (customerPortal: CustomerPortal) => Promise<void>;
}

export default function CopySnippet({ customerPortal, onUpdate }: Props) {
    const axios = useAxios();
    const snippetByEmail = getCustomerPortalSnippet(customerPortal, 'email');
    const snippetByStripeId = getCustomerPortalSnippet(customerPortal, 'stripeId');
    const standaloneWebpageUrl = `${SNIPPET_SERVER_URL}/portal/${customerPortal.id}`;
    const checkEvents = useCallback(() => {
        if (!axios || !axios.current) {
            return Promise.resolve([]);
        }
        return axios.current
            .get<Event[]>(`/customer-portals/${customerPortal.id}/events?limit=1&sinceMinsAgo=10`)
            .then((res) => res.data);
    }, [axios, customerPortal.id]);
    const [snippetDetected, handleCopyToClipboard, copied, showHelp] = useCopySnippet(checkEvents, 'customer_portal');

    return (
        <>
            <div className="flex flex-col w-2/3 mx-auto">
                <Header className="text-center py-8 mb-8">Embed your Customer Portal</Header>
                <SnippetFlexWrapper className="text-center">
                    <Tabs
                        tabs={[
                            {
                                title: 'Standard (Snippet)',
                                id: 'snippet',
                                body: (
                                    <SnippetContainerBox>
                                        <p className="pb-2">
                                            Copy the following into the <HtmlTagName name="body" /> tag of your HTML at
                                            the <span className="font-bold">exact</span> place you want your Customer
                                            Portal button to display.{' '}
                                            {!customerPortal.emailAuth && (
                                                <span>You can identify your user by email or Stripe Id.</span>
                                            )}
                                        </p>
                                        {!customerPortal.emailAuth && (
                                            <Tabs
                                                tabs={[
                                                    {
                                                        title: 'Email (default)',
                                                        id: 'email',
                                                        body: (
                                                            <div>
                                                                <SnippetBox
                                                                    snippet={snippetByEmail}
                                                                    onCopyToClipboard={handleCopyToClipboard}
                                                                />
                                                                <Warnings customerPortal={customerPortal} />
                                                            </div>
                                                        ),
                                                    },
                                                    {
                                                        title: 'Stripe Id',
                                                        id: 'stripeId',
                                                        body: (
                                                            <div>
                                                                <SnippetBox
                                                                    snippet={snippetByStripeId}
                                                                    onCopyToClipboard={handleCopyToClipboard}
                                                                />
                                                                <div className="mt-8">
                                                                    <Pill title="Tip" />
                                                                    <span className="ml-2">
                                                                        You need to replace{' '}
                                                                        <pre className="inline">
                                                                            CUSTOMER_STRIPE_ID_HERE
                                                                        </pre>{' '}
                                                                        with the Stripe Id of your customer.
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        )}

                                        {customerPortal.emailAuth && (
                                            <SnippetBox
                                                snippet={snippetByEmail}
                                                onCopyToClipboard={handleCopyToClipboard}
                                            />
                                        )}

                                        {snippetDetected && <SnippetDetected />}

                                        {copied && !snippetDetected && (
                                            <div className="flex place-items-center place-content-center">
                                                Checking for snippet...
                                                <Loader full={false} />
                                            </div>
                                        )}

                                        {showHelp && (
                                            <div className="pb-6">
                                                <Error
                                                    message={
                                                        <span>
                                                            We didn't detect your snippet yet. Need help?{' '}
                                                            <ContactSupportLink />
                                                        </span>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </SnippetContainerBox>
                                ),
                            },
                            {
                                title: 'Web Link',
                                id: 'web-link',
                                body: (
                                    <SnippetContainerBox>
                                        <p className="pb-2">
                                            {customerPortal.emailAuth && (
                                                <>
                                                    Send the following{' '}
                                                    <ExternalLink href={standaloneWebpageUrl}>link</ExternalLink> to
                                                    your customers who can enter their email address and receive a
                                                    direct link to the customer portal.
                                                </>
                                            )}
                                            {!customerPortal.emailAuth && (
                                                <span>Web link is only available when "Email Login" is enabled.</span>
                                            )}
                                        </p>
                                        {customerPortal.emailAuth && (
                                            <SnippetBox
                                                snippet={standaloneWebpageUrl}
                                                onCopyToClipboard={handleCopyToClipboard}
                                            />
                                        )}

                                        {snippetDetected && <SnippetDetected />}
                                    </SnippetContainerBox>
                                ),
                            },
                            {
                                title: 'Custom',
                                id: 'custom',
                                body: (
                                    <SnippetContainerBox>
                                        <p className="pb-2">
                                            Copy the following into the <HtmlTagName name="head" /> tag of your HTML.
                                            You will need to create your own custom button.
                                        </p>
                                        <div className="w-2/3">
                                            <SnippetBox
                                                snippet={getCustomerPortalScript(customerPortal)}
                                                onCopyToClipboard={handleCopyToClipboard}
                                            />
                                        </div>
                                        {snippetDetected && <SnippetDetected />}

                                        <p className="py-8">
                                            <span className="font-bold">Then</span> call this function when you want to
                                            trigger the Customer Portal
                                        </p>

                                        <SnippetBox snippet={getCustomTriggerPortalScript(customerPortal)} />

                                        {copied && !snippetDetected && (
                                            <div className="flex place-items-center place-content-center">
                                                Checking for snippet...
                                                <Loader full={false} />
                                            </div>
                                        )}

                                        {showHelp && (
                                            <div className="pb-6">
                                                <Error
                                                    message={
                                                        <span>
                                                            We didn't detect your snippet yet. Need help?{' '}
                                                            <ContactSupportLink />
                                                        </span>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </SnippetContainerBox>
                                ),
                            },
                            {
                                icon: <img src="/images/integrations/bubble-io.png" className="w-4" alt="" />,
                                title: 'Bubble',
                                id: 'bubble',
                                body: (
                                    <SnippetContainerBox>
                                        <h3 className="font-lg font-semibold text-lg underline mb-4">
                                            Install on Bubble.io
                                        </h3>
                                        <div className="text-left px-4 space-y-4">
                                            <p>
                                                Install the PriceWell{' '}
                                                <a
                                                    href="https://bubble.io/plugin/pricewell-1643279429035x716037702183813100"
                                                    className="link"
                                                >
                                                    Bubble Plugin
                                                </a>
                                            </p>
                                            <img
                                                src="https://help.pricewell.com/img/bubble-plugin.png"
                                                className="w-full mb-4"
                                                alt=""
                                            />
                                            <p className="py-4">
                                                Copy the Portal ID{' '}
                                                <CopyToClipboardLink name="ID" value={customerPortal.id} />
                                            </p>
                                            <p>
                                                Drag a Customer Portal element onto your Bubble app. Paste the ID into
                                                the "Customer Portal Id" field.
                                            </p>
                                            <p className="font-bold">
                                                Read the{' '}
                                                <a
                                                    href="https://help.pricewell.com/integrations/bubble/"
                                                    className="link"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    complete step-by-step documentation
                                                </a>
                                                .
                                            </p>
                                        </div>
                                    </SnippetContainerBox>
                                ),
                            },
                        ]}
                    />
                </SnippetFlexWrapper>
            </div>
            <Footer prev={{ href: ROUTES.CUSTOMER_PORTALS_CONFIG, disabled: false }} item={customerPortal} />
        </>
    );
}

function Warnings({ customerPortal }: Pick<Props, 'customerPortal'>) {
    return (
        <>
            <div className="mt-8">
                <Pill title="Tip" />
                <span className="ml-2">
                    You need to replace <pre className="inline">CUSTOMER_EMAIL_HERE</pre> with the email address of your
                    customer.
                </span>
            </div>
            {customerPortal.secureMode && (
                <div className="mt-8 flex">
                    <Pill title="Warning" colour="YELLOW" />
                    <span className="ml-2">
                        You have enabled secure mode <pre className="inline">CUSTOMER_EMAIL_HERE</pre> must be
                        encrypted. See the{' '}
                        <a
                            href="https://help.pricewell.com/features/customer_portal/secure-mode-advanced/"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="link"
                        >
                            Documentation
                        </a>{' '}
                        for more information.
                    </span>
                </div>
            )}
            {!customerPortal.secureMode && customerPortal.mode === 'live' && (
                <p className="mt-8 flex">
                    <Pill title="Warning" colour="RED" />
                    <span className="ml-2">
                        Your Customer Portal is <b>Live</b> but Secure Mode is not enabled. Please enable it from the
                        "Security" tab.
                    </span>
                </p>
            )}
        </>
    );
}

function SnippetDetected() {
    return (
        <div className="flex items-center justify-center pt-8">
            <Confetti recycle={false} />
            <img src={iconToUrl(FeatureIcon.GREEN)} alt="" className="mx-2 h-10" /> Detected
        </div>
    );
}
